/*
 * BuroEcl
 * Copyright (c) 2023 Buroclic. All rights reserved.
 * 
 * This file corresponds to the form
 * allowing the user to make a deposit of documents
 * using its customer space
 */

// Imports
import PropTypes from "prop-types";
import React, { useEffect, useState } from "react";
import { useDropzone } from 'react-dropzone';
import { getTypeImg } from "../../utils/Format";
import { FileSize, FileSizeUnit } from "../../../interfaces/IUtils";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faXmark } from "@fortawesome/free-solid-svg-icons";

export const DepositArea = (props) => {

    // Handle File input settings itself
    const [files, setFiles] = useState<File[]>([]);
    const { getRootProps, getInputProps } = useDropzone({
        maxFiles: props.maximum || 20,
        accept: props.allowedExts || {
            'image/*': ['.bmp', '.jpeg', '.jpg', '.png', '.gif', '.tif'],
            'application/msword': ['.doc', '.docx', '.docm', '.dotx', '.dotm', '.dot'],
            'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
            'application/vnd.oasis.opendocument.text': ['.odt'],
            'application/rtf': ['.rtf'],
            'application/pdf': ['.pdf'],
            'application/vnd.ms-excel': ['.xls'],
            'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx', '.xlsm', '.xlsb', '.xls', '.xltx', '.xltm', '.xlt', '.xps'],
            'application/vnd.oasis.opendocument.spreadsheet': ['.ods'],
            'text/csv': ['.csv'],
            'text/plain': ['.txt', '.md'],
            'application/xml': ['.xml']
        },
        onDrop: (acceptedFiles, fileRejections) => {
            setFiles(prevFiles => [...prevFiles, ...acceptedFiles]);

            if (fileRejections.length) {
                // TODO : Display a validation message using existing notification UI
                console.warn('Rejected files:', fileRejections);
            }
        }
    });


    // Files handling
    useEffect(() => {
        props.filesHandler(files);
    }, [files, props]);

    // Return the file size and appropriate unit
    const getFileSize = (file: File): FileSize => {
        // Octet
        const fileSize: FileSize = {
            size: file.size,
            unit: FileSizeUnit.octet
        }
        // Go
        if (file.size >= (1024 * 1024 * 1024)) {
            fileSize.size = parseInt((fileSize.size / (1024 * 1024 * 1024)).toString());
            fileSize.unit = FileSizeUnit.Go;
        }
        // Mo
        else if (file.size >= (1024 * 1024)) {
            fileSize.size = parseInt((fileSize.size / (1024 * 1024)).toString());
            fileSize.unit = FileSizeUnit.Mo;
        }
        // Ko
        else if (fileSize.size >= 1024) {
            fileSize.size = parseInt((fileSize.size / 1024).toString());
            fileSize.unit = FileSizeUnit.Ko;
        }

        return fileSize;
    }

    // Download the file from the tmp array
    const downloadFile = (index: number) => {
        console.log(files[index]);
        const file: File = files[index];

        // Create an 'a' Element in order to open the file into another tab
        const url = URL.createObjectURL(file)
        const link = document.createElement('a');
        link.href = url;
        link.download = file.name;
        document.body.appendChild(link);

        // Open document and remove URL / a element
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    }

    // Delete the file from the tmp array
    const deleteFile = (index: number) => {
        setFiles(prevFiles => prevFiles.filter((_, i) => i !== index));
    }


    //****************
    //  UI Rendering
    //***************/

    // returns whether kind of a helper or the files names
    const showFiles = (): React.ReactNode => {
        const rows: React.ReactNode[] = [];

        // Browse files in order to create rows of 4 documentsIl
        for (let i = 0; i < files.length; i += 4) {
            const fileRow = files.slice(i, i + 4);

            rows.push(
                <div key={'row-' + (i / 4)} className="flex flex-wrap gap-3">
                    {fileRow.map((file, index) => (
                        <div key={index + '-' + file.name} className="flex flex-row p-2 border rounded-md text-sm w-[23.6%] mb-3">
                            {/* Extension */}
                            <div className="self-center">{getTypeImg(file.name)}</div>

                            {/* File + Size */}
                            <div className="flex flex-col ml-2 whitespace-nowrap flex-grow overflow-hidden">
                                <span className="overflow-hidden text-ellipsis whitespace-nowrap" onClick={() => downloadFile(index + i)}>{file.name}</span>
                                <span className="text-gray-400">{getFileSize(file).size} {getFileSize(file).unit}</span>
                            </div>

                            {/* Delete */}
                            <FontAwesomeIcon icon={faXmark} className="h-6 w-6 text-red-500 ml-1 self-center cursor-pointer" onClick={() => deleteFile(index + i)} />
                        </div>
                    ))}
                </div>
            );
        }

        return rows;
    }

    return (
        <div className={`w-[inherit] flex flex-col ${props.className ?? ''}`} >

            {/* Show selected files in some rectangles */}
            <ul className="flex flex-col">
                {showFiles()}
            </ul>

            {/* The actual file input */}
            <div {...getRootProps()} className="'flex flex-col items-center justify-center w-full h-20 border-2 py-1 border-gray-300 border-dashed rounded-lg cursor-pointer bg-white'">
                <input {...getInputProps()} />
                <svg className="w-8 h-8 text-gray-500 dark:text-gray-400 mx-auto" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 20 16">
                    <path stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 13h3a3 3 0 0 0 0-6h-.025A5.56 5.56 0 0 0 16 6.5 5.5 5.5 0 0 0 5.207 5.021C5.137 5.017 5.071 5 5 5a4 4 0 0 0 0 8h2.167M10 15V6m0 0L8 8m2-2 2 2" />
                </svg>
                <p className="text-sm text-gray-500 dark:text-gray-400 text-center"><span className="font-semibold">Cliquez ici</span> pour déposer des documents, ou glissez-les ici ({props.maximum || 20} fichiers pour un total de {props.maximum || 50}Mo maximum)</p>
                <p className="text-xs text-gray-500 dark:text-gray-400 text-center">PDF, DOC, ODT, JPEG, PNG, XLS, ODS, TXT, CSV</p>
            </div>
        </div >
    );
};

DepositArea.propTypes = {
    className: PropTypes.string,
    maximum: PropTypes.number.isRequired,
    allowedExts: PropTypes.object.isRequired,
    filesHandler: PropTypes.func.isRequired

}